
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { devLog, devError } from "@/utils/devLog";

export interface CreatorStudent {
  student_id: string;
  student_email: string;
  student_name: string;
  course_id: string;
  course_title: string;
  enrolled_at: string;
  progress_percent: number;
  last_active: string | null;
  total_revenue: number;
}

export const useCreatorStudents = (creatorId: string | undefined) => {
  return useQuery<CreatorStudent[]>({
    queryKey: ["creatorStudents", creatorId],
    queryFn: async () => {
      if (!creatorId) {
        devLog("No creator ID provided to useCreatorStudents");
        return [];
      }
      
      devLog(`[CREATOR_STUDENTS] Fetching students for creator: ${creatorId}`);
      
      try {
        const { data, error } = await supabase.rpc("get_creator_students", {
          creator_id: creatorId,
        });
  
        if (error) {
          devError("[CREATOR_STUDENTS] Error fetching creator students:", error);
          throw new Error(`Failed to load creator students: ${error.message}`);
        }
  
        devLog(`[CREATOR_STUDENTS] Retrieved ${data?.length || 0} students from database`);
        
        if (data && data.length > 0) {
          devLog("[CREATOR_STUDENTS] First record from database:", data[0]);
          
          // Log all student IDs and emails
          devLog("[CREATOR_STUDENTS] All student records from database:");
          data.forEach((student, index) => {
            devLog(`[CREATOR_STUDENTS] DB Record ${index + 1}:`, {
              id: student.student_id,
              name: student.student_name,
              email: student.student_email,
              course: student.course_title,
              enrolledAt: student.enrolled_at
            });
          });
        }
  
        // Filter out records where the student_id matches the creator_id
        // This prevents creators from seeing themselves as students
        const filteredData = data?.filter(student => {
          const isCreator = student.student_id === creatorId;
          if (isCreator) {
            devLog(`[CREATOR_STUDENTS] Filtering out creator record: ${student.student_id}, ${student.student_email}`);
          }
          return !isCreator;
        }) || [];
        
        devLog(`[CREATOR_STUDENTS] After filtering creator's own data: ${filteredData.length} students`);
        if (filteredData.length > 0) {
          devLog("[CREATOR_STUDENTS] First filtered student:", filteredData[0]);
        } else {
          devLog("[CREATOR_STUDENTS] No students remain after filtering");
        }
  
        return filteredData;
      } catch (error) {
        devError("[CREATOR_STUDENTS] Exception in creator students fetch:", error);
        throw error;
      }
    },
    enabled: !!creatorId,
  });
};
